import React, {useCallback, useEffect, useState} from "react";
import {Box, useMediaQuery, useTheme, Stack, Typography, IconButton, Tooltip, Button} from "@mui/material";
import {useAtom, useSetAtom} from "jotai";
import {HugeiconsIcon} from "@hugeicons/react";
import {DatabaseIcon, File02Icon, Message01Icon, Layers01Icon, Refresh01Icon, Add01Icon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {MobileUnavailable} from "../../../components/MobileUnavailable";
import {KnowledgeManagerRepository} from "../../../repositories/KnowledgeManagerRepository";
import {Panel} from "../dashboard/components/Panel";
import {ServiceNotConfiguredBanner} from "../components/ServiceNotConfiguredBanner";
import {TopicListPanel} from "./components/TopicListPanel";
import {TopicDetailsPanel} from "./components/TopicDetailsPanel";
import {CreateTopicModalNew} from "./components/CreateTopicModalNew";
import {
  projectAtom,
  subProjectAtom,
  topicsAtom,
  selectedTopicAtom,
  topicsLoadingAtom,
} from "./atoms";

const StatTile = ({icon, color, label, value, note}) => (
  <Panel sx={{p: 2, flexDirection: "row", alignItems: "center", gap: 1.5}}>
    <Box sx={{
      width: 40, height: 40, borderRadius: "12px", flexShrink: 0, display: "flex", alignItems: "center",
      justifyContent: "center", color, bgcolor: `${color}1F`,
    }}>
      <HugeiconsIcon icon={icon} size={19}/>
    </Box>
    <Box sx={{minWidth: 0}}>
      <Typography noWrap sx={{fontSize: 13, fontWeight: 500, color: "#6B6B80"}}>{label}</Typography>
      <Typography sx={{fontSize: 24, fontWeight: 700, lineHeight: 1.2, letterSpacing: "-0.02em", color: "#23264D"}}>
        {value}
      </Typography>
      {note && <Typography noWrap sx={{fontSize: 12, color: "#8A8AA0"}}>{note}</Typography>}
    </Box>
  </Panel>
);

export const KnowledgeManagerPageRegistry = ({project, subProject}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [topics, setTopics] = useAtom(topicsAtom);
  const [, setSelectedTopic] = useAtom(selectedTopicAtom);
  const [loading, setLoading] = useAtom(topicsLoadingAtom);
  const setProjectAtom = useSetAtom(projectAtom);
  const setSubProjectAtom = useSetAtom(subProjectAtom);

  const [openCreate, setOpenCreate] = useState(false);

  // Sync route-driven project/language into atoms so child components can read them without prop drilling
  useEffect(() => {
    setProjectAtom(project);
  }, [project, setProjectAtom]);

  useEffect(() => {
    setSubProjectAtom(subProject);
  }, [subProject, setSubProjectAtom]);

  const fetchTopics = useCallback(async () => {
    if (!project?.id || !project?.backendConfiguration?.knowledgeManagerApi) return;
    try {
      setLoading(true);
      const list = await KnowledgeManagerRepository.listTopics({
        organizationId: project.organizationId,
        projectId: String(project.id),
      });
      setTopics(list);
    } catch (err) {
      toast.error(err.message || "Failed to load knowledge base topics");
      console.error("Error fetching topics:", err);
    } finally {
      setLoading(false);
    }
  }, [project?.id, project?.organizationId, project?.backendConfiguration?.knowledgeManagerApi, setLoading, setTopics]);

  useEffect(() => {
    fetchTopics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  // Clear selection when leaving the page so it doesn't leak into the next visit
  useEffect(() => {
    return () => setSelectedTopic(null);
  }, [setSelectedTopic]);

  const handleTopicCreated = async (topic) => {
    setOpenCreate(false);
    await fetchTopics();
    if (topic) setSelectedTopic(topic);
  };

  if (isMobile) {
    return <MobileUnavailable/>;
  }

  const totalTopics = topics.length;
  const totalDocuments = topics.reduce((sum, t) => sum + (t.documentCount || 0), 0);
  const totalQueries = topics.reduce((sum, t) => sum + (t.queryCount || 0), 0);
  const avgDocsPerTopic = totalTopics ? Math.round((totalDocuments / totalTopics) * 10) / 10 : 0;

  return (
    <Stack gap={5} sx={{
      p: {xs: 2, md: 3}, maxWidth: {xs: "calc(100% - 16px)", md: "calc(100% - 128px)"}, width: "100%", mx: "auto",
      boxSizing: "border-box", height: {xs: "auto", md: "100%"}, minHeight: 0,
    }}>

      <CreateTopicModalNew
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={handleTopicCreated}
      />

      <Stack direction={{xs: "column", md: "row"}} justifyContent={"space-between"} alignItems={{md: "flex-end"}} gap={2}>
        <Box>
          <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7", textTransform: "uppercase"}}>
            KNOWLEDGE BASE
          </Typography>
          <Typography component={"h1"} sx={{fontSize: 26, fontWeight: 700, color: "#23264D", letterSpacing: "-0.02em", mt: 0.5}}>
            Knowledge Manager
          </Typography>
          <Typography sx={{fontSize: 14, color: "#6B6B80", mt: 0.5}}>
            Organize topics, upload documents and query your project's knowledge base
          </Typography>
        </Box>
        <Stack direction="row" gap={1} alignItems="center">
          <Button
            size="small"
            startIcon={<HugeiconsIcon icon={Add01Icon} size={16}/>}
            onClick={() => setOpenCreate(true)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 13,
              borderRadius: '10px',
              color: '#374151',
              bgcolor: '#FAFAFD',
              '&:hover': {bgcolor: '#ECECF3'},
            }}
          >
            New Topic
          </Button>
          <Tooltip title={"Refresh"}>
            <IconButton onClick={fetchTopics} disabled={loading} sx={{borderRadius: "50% !important", color: "#8A8AA0"}}>
              <HugeiconsIcon icon={Refresh01Icon} size={17}/>
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {!project?.backendConfiguration?.knowledgeManagerApi && (
        <ServiceNotConfiguredBanner label={"Knowledge Manager API"}/>
      )}

      <Box sx={{
        display: "grid",
        gridTemplateColumns: {xs: "1fr", md: "380px 1fr"},
        gridTemplateRows: {xs: "auto auto auto", md: "auto 1fr"},
        gridTemplateAreas: {
          xs: `"stats" "sidebar" "details"`,
          md: `"sidebar stats" "sidebar details"`,
        },
        gap: 3,
        flex: 1,
        minHeight: {xs: "calc(100vh - 260px)", md: 0},
      }}>
        <Box sx={{gridArea: "sidebar", display: "flex", flexDirection: "column", overflow: "hidden", bgcolor: "#fff", border: "1px solid #ECECF3", borderRadius: "14px", minWidth: 0, minHeight: 0}}>
          <TopicListPanel onAddClick={() => setOpenCreate(true)} onRefresh={fetchTopics}/>
        </Box>

        <Box sx={{gridArea: "stats", display: "grid", gap: 2.5, gridTemplateColumns: {xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))"}}}>
          <StatTile icon={DatabaseIcon} color={"#8491F7"} label={"Topics"} value={totalTopics}
                    note={totalTopics ? `Avg ${avgDocsPerTopic} docs per topic` : "No topics yet"}/>
          <StatTile icon={File02Icon} color={"#2F9A6A"} label={"Documents"} value={totalDocuments}
                    note={"Indexed across all topics"}/>
          <StatTile icon={Message01Icon} color={"#D9A13A"} label={"Queries"} value={totalQueries}
                    note={"Total questions asked"}/>
          <StatTile icon={Layers01Icon} color={"#3D86C6"} label={"Sub projects"} value={project?.subProjects?.length || 0}
                    note={"Available in this project"}/>
        </Box>

        <Box sx={{gridArea: "details", display: "flex", flexDirection: "column", overflow: "hidden", bgcolor: "#fff", border: "1px solid #ECECF3", borderRadius: "14px", minWidth: 0, minHeight: 0}}>
          <TopicDetailsPanel onTopicChanged={fetchTopics}/>
        </Box>
      </Box>
    </Stack>
  );
};
